/**
 * Колокольчик сотрудника (R-117): `GET /admin/notifications`,
 * `GET /admin/notifications/unread-count`, `POST /admin/notifications/read`,
 * проверка `POST /admin/notifications/staff-test` и браузер для push —
 * `POST /admin/push/tokens`. Всё открывает право `orders:r`.
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ApiClient } from '@dorixona/api'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import {
  toNotification,
  type StaffNotification,
  type StaffNotificationDto,
  type StaffTestDto,
} from './model'

/**
 * Язык уведомлений. Переключателя языка в админке нет, она по-русски, а общий
 * клиент `X-Lang` не шлёт. Заголовки списка и текст push сервер пишет на языке
 * этого заголовка, поэтому он — у списка и у регистрации браузера.
 */
export const STAFF_LANG = 'ru'
const LANG_HEADERS = { 'X-Lang': STAFF_LANG }

/** Как часто перечитывать счётчик, даже если живые события молчат. */
export const UNREAD_POLL_MS = 60_000

/** Больше сервер в одну страницу не отдаёт. */
export const MAX_PAGE_SIZE = 100

/**
 * Сколько страниц по 100 листаем ради «Только непрочитанные»: фильтра на
 * сервере нет. Тысячи непрочитанных хватит с запасом.
 */
export const UNREAD_SCAN_PAGES = 10

export interface NotificationsPage {
  items: StaffNotification[]
  /** Непрочитанных всего — сервер отдаёт это число в `total` списка. */
  unread: number
  /**
   * Сколько всего уведомлений. Сервер этого не говорит: известно точно, только
   * когда страница неполная (последняя). Иначе — «хотя бы на одно больше».
   */
  total: number
  exact: boolean
}

interface ListDto {
  items?: StaffNotificationDto[]
  total?: number
}

async function fetchList(api: ApiClient, page: number, pageSize: number) {
  const dto = await api.get<ListDto>('/admin/notifications', {
    query: { page, page_size: pageSize },
    headers: LANG_HEADERS,
  })
  return { items: (dto.items ?? []).map(toNotification), unread: dto.total ?? 0 }
}

/** Страница списка, новые сверху. */
export async function fetchNotifications(
  api: ApiClient,
  page: number,
  pageSize: number,
): Promise<NotificationsPage> {
  const { items, unread } = await fetchList(api, page, pageSize)
  const full = items.length === pageSize
  return {
    items,
    unread,
    total: (page - 1) * pageSize + items.length + (full ? 1 : 0),
    exact: !full,
  }
}

/**
 * Только непрочитанные. Фильтра на сервере нет: листаем по 100, пока не
 * соберём столько, сколько сервер насчитал, — тогда итог точный и страницы
 * листаются на месте.
 */
export async function fetchUnread(api: ApiClient): Promise<NotificationsPage> {
  const found: StaffNotification[] = []
  let unread = 0
  for (let page = 1; page <= UNREAD_SCAN_PAGES; page += 1) {
    const next = await fetchList(api, page, MAX_PAGE_SIZE)
    unread = next.unread
    found.push(...next.items.filter((n) => !n.read))
    if (found.length >= unread || next.items.length < MAX_PAGE_SIZE) break
  }
  return { items: found, unread, total: found.length, exact: true }
}

export interface NotificationsQuery {
  page: number
  pageSize: number
  unreadOnly: boolean
}

/** Ключ первой страницы: её же берёт тост о новом уведомлении. */
export function pageKey(page: number, pageSize: number) {
  return keys.notifications({ page, pageSize })
}

export function useNotifications({ page, pageSize, unreadOnly }: NotificationsQuery) {
  const api = useApi()
  return useQuery({
    queryKey: unreadOnly ? keys.notifications({ unreadOnly }) : pageKey(page, pageSize),
    queryFn: () => (unreadOnly ? fetchUnread(api) : fetchNotifications(api, page, pageSize)),
    // Непрочитанные собраны целиком — страницу вырезаем здесь же.
    select: (data: NotificationsPage) =>
      unreadOnly
        ? { ...data, items: data.items.slice((page - 1) * pageSize, page * pageSize) }
        : data,
    placeholderData: keepPreviousData,
  })
}

/** Число непрочитанных для меню. Перечитывается само, при возврате во вкладку и по событиям. */
export function useUnreadCount(enabled: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: keys.notificationsUnread,
    queryFn: async () =>
      (await api.get<{ count?: number }>('/admin/notifications/unread-count')).count ?? 0,
    enabled,
    refetchInterval: UNREAD_POLL_MS,
    refetchOnWindowFocus: true,
  })
}

function markPage(page: NotificationsPage, ids: number[]): NotificationsPage {
  const all = ids.length === 0
  let marked = 0
  const items = page.items.map((n) => {
    if (n.read || !(all || ids.includes(n.id))) return n
    marked += 1
    return { ...n, read: true }
  })
  return { ...page, items, unread: all ? 0 : Math.max(0, page.unread - marked) }
}

/**
 * «Прочитано»: одно уведомление или пустой список — все. Список и счётчик
 * меняются сразу, после ответа сервера перечитываются — правда за ним.
 */
export function useMarkRead() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (ids: number[]) => api.post('/admin/notifications/read', { ids }),
    onMutate: async (ids) => {
      await client.cancelQueries({ queryKey: keys.notificationsAll })
      client.setQueriesData<NotificationsPage>({ queryKey: keys.notificationsList }, (page) =>
        page ? markPage(page, ids) : page,
      )
      client.setQueryData<number>(keys.notificationsUnread, (count) =>
        count === undefined ? count : ids.length === 0 ? 0 : Math.max(0, count - ids.length),
      )
    },
    onSettled: () => client.invalidateQueries({ queryKey: keys.notificationsAll }),
  })
}

/** Проверка: тестовое уведомление себе — в список и push на свои браузеры. */
export function useStaffTest() {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: () => api.post<StaffTestDto>('/admin/notifications/staff-test'),
    onSuccess: () => client.invalidateQueries({ queryKey: keys.notificationsAll }),
  })
}

/** Запомнить браузер для push. `X-Lang` — язык, на котором сервер пишет push. */
export async function registerPushToken(api: ApiClient, token: string): Promise<void> {
  await api.post('/admin/push/tokens', { token, platform: 'web' }, { headers: LANG_HEADERS })
}
